import * as THREE from 'three';
import type { FeatureType, PackedLayer } from '../slice/types';
import { FEATURE_COLORS, FEATURE_TYPES } from '../slice/types';
import type { PreviewState } from '../preview/previewStore';

function packedToSegments(xy: Float32Array, z: number, out: number[], fraction = 1) {
  const pts: number[] = [];
  const emit = () => {
    for (let k = 0; k + 3 < pts.length; k += 2) {
      const x0 = pts[k], y0 = pts[k + 1], x1 = pts[k + 2], y1 = pts[k + 3];
      if (![x0, y0, x1, y1].every(Number.isFinite)) continue;
      const dx = x1 - x0, dy = y1 - y0;
      const len = Math.hypot(dx, dy);
      if (len < 0.08 || len > 180) continue;
      out.push(x0, z, y0, x1, z, y1);
    }
  };
  for (let i = 0; i < xy.length; i += 2) {
    if (!Number.isFinite(xy[i]) || !Number.isFinite(xy[i + 1])) {
      if (pts.length >= 4) emit();
      pts.length = 0;
      continue;
    }
    pts.push(xy[i], xy[i + 1]);
  }
  if (pts.length >= 4) emit();
  if (fraction < 1 && out.length) {
    const keep = Math.max(6, Math.floor((out.length * fraction) / 6) * 6);
    out.length = keep;
  }
}

function makeMat(color: number) {
  return new THREE.MeshBasicMaterial({
    color,
    toneMapped: false,
    depthTest: true,
    depthWrite: true,
    side: THREE.DoubleSide,
  });
}

interface Batch {
  mesh: THREE.InstancedMesh;
  mat: THREE.MeshBasicMaterial;
  cap: number;
}

export class ToolpathRenderer {
  group = new THREE.Group();
  private past: Partial<Record<FeatureType, Batch>> = {};
  private current: Partial<Record<FeatureType, Batch>> = {};
  private lastKey = '';
  private unpacked: Array<Partial<Record<FeatureType, number[]>>> = [];
  private unpackedLen = 0;
  private dummy = new THREE.Object3D();
  private fromX = new THREE.Vector3(1, 0, 0);
  private dir = new THREE.Vector3();
  private quat = new THREE.Quaternion();

  constructor() {
    this.group.name = 'toolpaths';
    for (const f of FEATURE_TYPES) {
      this.past[f] = this.makeBatch(FEATURE_COLORS[f], 512, 2);
      this.current[f] = this.makeBatch(FEATURE_COLORS[f], 512, 3);
    }
  }

  private makeBatch(color: number, cap: number, renderOrder: number): Batch {
    const geom = new THREE.BoxGeometry(1, 1, 1);
    const mat = makeMat(color);
    const mesh = new THREE.InstancedMesh(geom, mat, cap);
    mesh.count = 0;
    mesh.frustumCulled = false;
    mesh.renderOrder = renderOrder;
    mesh.visible = false;
    this.group.add(mesh);
    return { mesh, mat, cap };
  }

  private ensure(batch: Batch, color: number, n: number): Batch {
    if (n <= batch.cap) return batch;
    let cap = batch.cap;
    while (cap < n) cap *= 2;
    this.group.remove(batch.mesh);
    batch.mesh.geometry.dispose();
    const next = this.makeBatch(color, cap, batch.mesh.renderOrder);
    next.mat.color.copy(batch.mat.color);
    batch.mat.dispose();
    return next;
  }

  private write(batch: Batch, segs: number[], width: number, height: number) {
    const n = (segs.length / 6) | 0;
    batch.mesh.count = n;
    if (!n) {
      batch.mesh.visible = false;
      return;
    }
    const dummy = this.dummy;
    const dir = this.dir;
    const quat = this.quat;
    for (let i = 0; i < n; i++) {
      const o = i * 6;
      const x0 = segs[o], y0 = segs[o + 1], z0 = segs[o + 2];
      const x1 = segs[o + 3], y1 = segs[o + 4], z1 = segs[o + 5];
      const dx = x1 - x0;
      const dz = z1 - z0;
      const len = Math.hypot(dx, dz);
      dummy.position.set((x0 + x1) * 0.5, (y0 + y1) * 0.5, (z0 + z1) * 0.5);
      if (len < 1e-6) {
        dummy.scale.set(0, 0, 0);
      } else {
        dir.set(dx / len, 0, dz / len);
        quat.setFromUnitVectors(this.fromX, dir);
        dummy.quaternion.copy(quat);
        dummy.scale.set(len, height, width);
      }
      dummy.updateMatrix();
      batch.mesh.setMatrixAt(i, dummy.matrix);
    }
    batch.mesh.instanceMatrix.needsUpdate = true;
    batch.mesh.visible = true;
  }

  setSize(_w: number, _h: number) {
    /* world-space traces do not need a resolution uniform */
  }

  dispose() {
    for (const f of FEATURE_TYPES) {
      this.past[f]?.mesh.geometry.dispose();
      this.past[f]?.mat.dispose();
      this.current[f]?.mesh.geometry.dispose();
      this.current[f]?.mat.dispose();
    }
  }

  update(preview: PreviewState, filamentHex: number, lineWidth: number) {
    const layers = preview.layers;
    const idx = Math.min(preview.currentLayer, Math.max(0, layers.length - 1));
    const key = `${layers.length}:${idx}:${preview.layerDraw}:${preview.colorBy}:${filamentHex}:${JSON.stringify(preview.hidden)}:${lineWidth}:${preview.status}`;
    if (key === this.lastKey) return;
    const width = Math.max(0.34, lineWidth || 0.42);
    const pastW = width * 0.92;
    const thick = Math.max(0.06, (layers[idx]?.height || 0.2) * 0.55);

    if (layers.length < this.unpackedLen) {
      this.unpacked = [];
      this.unpackedLen = 0;
    }
    for (let i = this.unpackedLen; i < layers.length; i++) {
      const segs: Partial<Record<FeatureType, number[]>> = {};
      for (const f of FEATURE_TYPES) {
        const packed = layers[i]?.paths[f];
        const arr: number[] = [];
        if (packed && packed.length) packedToSegments(packed, layers[i].z, arr, 1);
        segs[f] = arr;
      }
      this.unpacked[i] = segs;
    }
    this.unpackedLen = layers.length;

    for (const f of FEATURE_TYPES) {
      const hidden = !!preview.hidden[f];
      const color = preview.colorBy === 'filament' ? filamentHex : FEATURE_COLORS[f];
      let p = this.past[f]!;
      let c = this.current[f]!;
      p.mat.color.setHex(color).multiplyScalar(0.58);
      c.mat.color.setHex(color);

      if (hidden || !layers.length) {
        p.mesh.visible = false;
        c.mesh.visible = false;
        this.past[f] = p;
        this.current[f] = c;
        continue;
      }

      const pastPts: number[] = [];
      const showPast = preview.status !== 'slicing';
      if (showPast) {
        for (let i = 0; i < idx; i++) {
          const src = this.unpacked[i]?.[f];
          if (src && src.length) pastPts.push(...src);
        }
      }
      p = this.ensure(p, color, (pastPts.length / 6) | 0);
      this.write(p, pastPts, pastW, thick * 0.85);

      const curPts: number[] = [];
      const src = this.unpacked[idx]?.[f];
      if (src && src.length) {
        if (preview.layerDraw >= 0.999) curPts.push(...src);
        else {
          const packed = layers[idx]?.paths[f];
          if (packed && packed.length) packedToSegments(packed, layers[idx].z, curPts, preview.layerDraw);
        }
      }
      c = this.ensure(c, color, (curPts.length / 6) | 0);
      this.write(c, curPts, width, thick);
      this.past[f] = p;
      this.current[f] = c;
    }
    this.lastKey = key;
  }

  lengthsUpTo(layers: PackedLayer[], index: number): number {
    let sum = 0;
    for (let i = 0; i <= index && i < layers.length; i++) {
      const L = layers[i].lengths;
      if (!L) continue;
      for (const k of FEATURE_TYPES) sum += L[k] || 0;
    }
    return sum;
  }
}
